import { RefObject, useEffect } from 'react'
import { GanttDisplayOptions } from '../../../../../../../../../domain/value-object/GanttParameterVO'

type UseScrollTrackingOptions = {
  /** insideラベル && V2 の場合のみ追従を有効化 */
  enabled: boolean
  trackLeftPercent: number
  trackRightPercent: number
  displayOptions?: GanttDisplayOptions
}

/**
 * insideラベルをスクロールに追従させるカスタムフック。
 * DOM直接操作でre-renderを避ける。
 *
 * 列追加/削除時にAG Gridが cell.style.left を更新するが、
 * getBoundingClientRect() はレイアウト反映前の古い値を返すことがあるため、
 * cell.style.left の CSS値を直接読んで位置計算する。
 */
export const useScrollTracking = (
  wrapperRef: RefObject<HTMLElement>,
  labelRef: RefObject<HTMLElement>,
  {
    enabled,
    trackLeftPercent,
    trackRightPercent,
    displayOptions,
  }: UseScrollTrackingOptions
): void => {
  useEffect(() => {
    if (!enabled) return
    const wrapper = wrapperRef.current
    const label = labelRef.current
    if (!wrapper || !label) return

    const scrollContainer = wrapper.closest<HTMLElement>(
      '.ag-center-cols-viewport'
    )
    if (!scrollContainer) return

    const cell = wrapper.closest<HTMLElement>('.ag-cell')

    const updatePosition = () => {
      const currentCellWidth = wrapper.offsetWidth
      // cell.style.leftはAG Gridが管理するセルの絶対位置（center viewport内）。
      // getBoundingClientRect()は列変更直後にレイアウトが追いつかず古い値を返すことがあるため、
      // AG Gridが直接設定するCSS値を使う。
      const cellOffsetLeft = parseFloat(cell?.style.left || '0')
      const barLeftPx = (Math.max(0, trackLeftPercent) / 100) * currentCellWidth
      const barRightPx =
        (Math.min(100, trackRightPercent) / 100) * currentCellWidth

      const visibleCellLeft = Math.max(
        0,
        scrollContainer.scrollLeft - cellOffsetLeft
      )
      const effectiveLeftPx = Math.max(barLeftPx, visibleCellLeft) + 4
      const availableWidth = barRightPx - effectiveLeftPx - 4

      if (availableWidth <= 0) {
        label.style.opacity = '0'
        label.style.pointerEvents = 'none'
      } else {
        label.style.opacity = '1'
        label.style.pointerEvents = 'all'
        label.style.left = `${effectiveLeftPx}px`
        label.style.width = `${availableWidth}px`
      }
    }

    const onScroll = () => updatePosition()
    scrollContainer.addEventListener('scroll', onScroll, { passive: true })

    // 列追加/削除で.ag-cellのstyle(left, width等)が複数回に分けて更新される。
    // AG GridはscrollLeftを先に更新し、cell.style.leftを後から更新するため、
    // 最初のstyle変更時にupdatePositionすると不整合な値を読んでしまう。
    // rAFでdebounceして、同一フレーム内の全style変更が完了してから再計算する。
    let cellStyleRafId = 0
    const cellObserver = new MutationObserver(() => {
      cancelAnimationFrame(cellStyleRafId)
      cellStyleRafId = requestAnimationFrame(() => {
        updatePosition()
      })
    })
    if (cell) {
      cellObserver.observe(cell, {
        attributes: true,
        attributeFilter: ['style'],
      })
    }

    // 依存値変更時（日/週/月切替・displayOptions変更等）は位置を再計算
    // rAFで遅延させてDOM更新完了後に実行
    const rafId = requestAnimationFrame(() => {
      updatePosition()
    })
    return () => {
      cancelAnimationFrame(rafId)
      cancelAnimationFrame(cellStyleRafId)
      cellObserver.disconnect()
      scrollContainer.removeEventListener('scroll', onScroll)
      // insideモード解除時にDOM直接操作のinlineスタイルをリセット
      label.style.left = ''
      label.style.width = ''
      label.style.opacity = ''
      label.style.pointerEvents = ''
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- wrapperRef/labelRefはRefObjectで不変
  }, [enabled, trackLeftPercent, trackRightPercent, displayOptions])
}
